'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import {
  AlertTriangle, DollarSign, MessageSquare, FileText, CheckCircle,
  Clock, Phone, X, ChevronDown
} from 'lucide-react';

const formatVT = (n: number) => `VT ${n.toLocaleString('en-US')}`;

const overdueLoans = [
  { id: 'VL-2026-004', borrower: 'Sam Kaltonga', phone: '+678 555 1005', dueDate: '2026-06-15', daysOverdue: 51, principal: 280000, totalDue: 336000, paid: 100000, remaining: 236000, lateFee: 23600, collectionNotes: ['Called 2026-07-01 — no answer', 'SMS sent 2026-07-05'] },
  { id: 'VL-2026-008', borrower: 'Rex Naupa', phone: '+678 555 1003', dueDate: '2026-07-10', daysOverdue: 26, principal: 100000, totalDue: 120000, paid: 0, remaining: 120000, lateFee: 6000, collectionNotes: ['Visited office 2026-07-15'] },
  { id: 'VL-2026-009', borrower: 'Mele Tari', phone: '+678 555 1009', dueDate: '2026-07-28', daysOverdue: 8, principal: 150000, totalDue: 180000, paid: 60000, remaining: 120000, lateFee: 3600, collectionNotes: [] },
  { id: 'VL-2026-010', borrower: 'Hemi Vira', phone: '+678 555 1010', dueDate: '2026-07-30', daysOverdue: 6, principal: 80000, totalDue: 96000, paid: 0, remaining: 96000, lateFee: 2880, collectionNotes: [] },
];

const getAgingBucket = (days: number) => {
  if (days <= 7) return { label: '1–7 days', color: 'text-warning', bg: 'bg-warning/10', border: 'border-warning/20' };
  if (days <= 30) return { label: '8–30 days', color: 'text-orange-400', bg: 'bg-orange-400/10', border: 'border-orange-400/20' };
  return { label: '30+ days', color: 'text-negative', bg: 'bg-negative/10', border: 'border-negative/20' };
};

type ModalType = 'note' | 'fee' | 'extend' | null;

export default function OverduePage() {
  const [modal, setModal] = useState<ModalType>(null);
  const [selected, setSelected] = useState<typeof overdueLoans[0] | null>(null);
  const [noteText, setNoteText] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const totalOverdue = overdueLoans.reduce((s, l) => s + l.remaining, 0);
  const totalFees = overdueLoans.reduce((s, l) => s + l.lateFee, 0);

  const bucket1 = overdueLoans.filter(l => l.daysOverdue <= 7);
  const bucket2 = overdueLoans.filter(l => l.daysOverdue > 7 && l.daysOverdue <= 30);
  const bucket3 = overdueLoans.filter(l => l.daysOverdue > 30);

  const openModal = (type: ModalType, loan: typeof overdueLoans[0]) => {
    setSelected(loan);
    setNoteText('');
    setModal(type);
  };

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Overdue</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Delinquent account management — aging buckets and collection tools</p>
          </div>
        </div>

        {/* Summary KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-card border border-negative/20 rounded-xl p-4">
            <p className="text-xs text-muted-foreground font-medium mb-1">Total Overdue</p>
            <p className="text-xl font-bold text-negative font-mono">{formatVT(totalOverdue)}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{overdueLoans.length} accounts</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <p className="text-xs text-muted-foreground font-medium mb-1">1–7 Days</p>
            <p className="text-xl font-bold text-warning">{bucket1.length}</p>
            <p className="text-xs text-muted-foreground mt-0.5">Early stage</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <p className="text-xs text-muted-foreground font-medium mb-1">8–30 Days</p>
            <p className="text-xl font-bold text-orange-400">{bucket2.length}</p>
            <p className="text-xs text-muted-foreground mt-0.5">Active collection</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <p className="text-xs text-muted-foreground font-medium mb-1">30+ Days</p>
            <p className="text-xl font-bold text-negative">{bucket3.length}</p>
            <p className="text-xs text-muted-foreground mt-0.5">Critical</p>
          </div>
        </div>

        {/* Aging Buckets */}
        {[
          { label: '🔴 30+ Days Overdue', loans: bucket3, urgency: 'critical' },
          { label: '🟠 8–30 Days Overdue', loans: bucket2, urgency: 'high' },
          { label: '🟡 1–7 Days Overdue', loans: bucket1, urgency: 'medium' },
        ].filter(b => b.loans.length > 0).map(bucket => (
          <div key={bucket.label} className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <h2 className="font-semibold text-foreground text-sm">{bucket.label}</h2>
              <span className="text-xs text-muted-foreground">{bucket.loans.length} account{bucket.loans.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="divide-y divide-border">
              {bucket.loans.map(loan => {
                const aging = getAgingBucket(loan.daysOverdue);
                const isExpanded = expandedId === loan.id;
                return (
                  <div key={loan.id} className="hover:bg-muted/20 transition-colors">
                    <div className="px-4 py-4">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${aging.bg} border ${aging.border}`}>
                            <AlertTriangle size={16} className={aging.color} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-foreground">{loan.borrower}</p>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${aging.bg} ${aging.color}`}>{loan.daysOverdue} days overdue</span>
                            </div>
                            <div className="flex items-center gap-3 mt-0.5">
                              <span className="text-xs text-muted-foreground font-mono">{loan.id}</span>
                              <span className="flex items-center gap-1 text-xs text-muted-foreground"><Phone size={11} /> {loan.phone}</span>
                              <span className="flex items-center gap-1 text-xs text-muted-foreground"><Clock size={11} /> Due: {loan.dueDate}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-mono font-bold text-negative text-sm">{formatVT(loan.remaining)}</p>
                            <p className="text-xs text-muted-foreground">+ {formatVT(loan.lateFee)} late fee</p>
                          </div>
                          <button onClick={() => setExpandedId(isExpanded ? null : loan.id)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground transition-colors">
                            <ChevronDown size={16} className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                          </button>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-wrap gap-2 mt-3">
                        <button onClick={() => openModal('fee', loan)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-negative/10 text-negative text-xs font-semibold hover:bg-negative/20 transition-colors">
                          <DollarSign size={12} /> Apply Late Fee
                        </button>
                        <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-muted text-muted-foreground text-xs font-medium hover:text-foreground transition-colors">
                          <MessageSquare size={12} /> Send Reminder
                        </button>
                        <button onClick={() => openModal('note', loan)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-muted text-muted-foreground text-xs font-medium hover:text-foreground transition-colors">
                          <FileText size={12} /> Log Collection Note
                        </button>
                        <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-positive/10 text-positive text-xs font-semibold hover:bg-positive/20 transition-colors">
                          <CheckCircle size={12} /> Mark as Settled
                        </button>
                        <button onClick={() => openModal('extend', loan)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-info/10 text-info text-xs font-semibold hover:bg-info/20 transition-colors">
                          <Clock size={12} /> Extend Loan Term
                        </button>
                      </div>
                    </div>

                    {/* Expanded: Collection Notes */}
                    {isExpanded && (
                      <div className="px-4 pb-4 pt-0">
                        <div className="p-3 rounded-xl bg-muted/30 border border-border">
                          <p className="text-xs font-semibold text-muted-foreground mb-2">Collection Activity Log</p>
                          {loan.collectionNotes.length === 0 ? (
                            <p className="text-xs text-muted-foreground italic">No collection notes yet</p>
                          ) : (
                            <div className="space-y-1">
                              {loan.collectionNotes.map((note, i) => (
                                <div key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                                  <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground mt-1.5 shrink-0" />
                                  {note}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Modals */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <h3 className="font-bold text-foreground">
                {modal === 'note' && 'Log Collection Note'}
                {modal === 'fee' && 'Apply Late Fee'}
                {modal === 'extend' && 'Extend Loan Term'}
              </h3>
              <button onClick={() => setModal(null)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground"><X size={16} /></button>
            </div>
            <div className="p-6 space-y-4">
              {selected && (
                <div className="p-3 rounded-lg bg-muted/30 text-sm">
                  <span className="text-muted-foreground">Loan: </span>
                  <span className="font-semibold text-foreground">{selected.id} — {selected.borrower}</span>
                </div>
              )}
              {modal === 'note' && (
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Collection Note</label>
                  <textarea value={noteText} onChange={e => setNoteText(e.target.value)} rows={4} placeholder="Describe collection action taken..." className="w-full px-3 py-2 rounded-lg bg-input border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary resize-none" />
                </div>
              )}
              {modal === 'fee' && selected && (
                <div className="space-y-3">
                  <div className="p-3 rounded-lg bg-negative/5 border border-negative/20">
                    <p className="text-xs text-muted-foreground">Calculated Late Fee (10% of remaining)</p>
                    <p className="text-xl font-bold text-negative font-mono mt-1">{formatVT(selected.lateFee)}</p>
                  </div>
                  <p className="text-xs text-muted-foreground">This fee will be added to the outstanding balance and recorded in the general ledger.</p>
                </div>
              )}
              {modal === 'extend' && (
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Extend by (days)</label>
                  <div className="flex gap-2">
                    {[7, 14, 30].map(d => (
                      <button key={d} className="flex-1 py-2 rounded-lg text-sm font-medium border border-border text-muted-foreground hover:border-primary/50 hover:text-foreground transition-colors">{d} days</button>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button onClick={() => setModal(null)} className="flex-1 px-4 py-2 rounded-lg border border-border text-sm font-medium text-muted-foreground hover:bg-muted transition-colors">Cancel</button>
                <button onClick={() => setModal(null)} className="flex-1 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors flex items-center justify-center gap-2">
                  <CheckCircle size={15} /> Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
